import { DataLinkAttachmentClient, DocumentLibDto, LinkedAttachmentDto } from "@app/api";
import { CerFormContext } from "@cer/cer-form/cer-form-context";
import { CerFormView, ICerViewComponent } from "@cer/cer-form/cer-form-view";
import { uiCmdBack, uiCmdFolderAdd, uiCmdUpload } from "@cer/cer-ui/cer-ui-commands";
import { UiActionTypeEnum, UiCommand, UiCommandEvent } from "@cer/cer-ui/cer-ui-types";

import { DocumentLibAttachmentView } from "./document-lib-attachment-view";
import { docTypeFolder, iconImageFolderHtml, iconImageTextHtml, isDocTypeFolder, renderFileExtension } from "@cer/cer-form/cer-form-view-type/cer-form-view-type-document/cer-form-view-document-type";
import { ParentDataArgs } from "@cer/cer-form/cer-form.service";
import { CerGridComponent } from "@cer/cer-grid/cer-grid.component";
import { documentLibEntityName } from "./document-lib.form";
import { CerDataService } from "@cer/cer-data/cer-data.service";

const uiCmdBackShort: UiCommand = Object.assign({}, uiCmdBack, { text: 'Tilbage' })

export class DocumentLibFolderDto {
  constructor(
    public name: string,
    public setAsRoot: boolean
  ) { }
}

export class DocumentLibFinderView extends CerFormView {

  constructor(private docLib: DocumentLibDto, private attachmentView: DocumentLibAttachmentView, parentView: CerFormView, private context: CerFormContext) {

    const apiParams = [
      '$entity=DocumentLib',
      "$rowId=" + docLib.id,
      "$token=" + docLib.token,
      "$includeSubFolders=true",
      "$getList=true"
    ];

    super({
      title: docLib.name,
      titleHeaderVisible: true,
      viewMetadata: {
        dataApiUrl: 'api/DataLinkAttachment',
        dataApiParams: apiParams,
        attachmentUploadUrl: ((e, overwriteExisting) => { return this.attachmentUploadUrlGet(overwriteExisting) }),
        dataOnParentDataOnly: true,
        allowEdit: docLib.allowEdit === true,
        allowCreate: docLib.allowCreate === true,
        allowDelete: docLib.allowDelete === true,
        text: docLib.name,
        textSingular: 'Dokument',
        primaryKey: 'id',
        titleFields: ['fileName'],
        baseFields: ['id'],
      },
      fieldMetadata: [
        {
          name: 'originalDocType', text: 'Type', width: '40px', format: 'TextCenter',
          allowEdit: false, visibleEdit: false, defaultValue: docTypeFolder,
          onRenderCell: (e) => { renderFileExtension(e) }
        },
        { name: 'originalFileName', text: 'Navn', width: '200px', },
        { name: 'originalFileExtension', text: 'Efternavn', visible: false, allowEdit: false, visibleEdit: true },
        { name: 'folderName', text: 'Folder', visible: false, visibleEdit: false },
        { name: 'created', text: 'Oprettet', format: 'DateTime', allowEdit: false,visibleEdit: false },
        { name: 'modified', text: 'Ændret', format: 'DateTime', allowEdit: false, visible: false,visibleEdit: false }
      ],
      toolbarCommands: [uiCmdBackShort],
      onCommandEvent: (e) => (this.onCommand(e))
    });
    if (this.viewMetadata.allowCreate) {
      this.toolbarCommands.push(uiCmdUpload);
      this.toolbarCommands.push(uiCmdFolderAdd);
    }
    // Enable folder navigation 
    this.id = this.getId();
    this.dataChild = true;
    this.dataParent = false;
    this.viewMetadata.onParentValueGet = (e) => this.onParentValueGet(e);
    this.viewMetadata.parentForeignKey = '$folderName';
    this.dataChildOfIds = parentView?.id.length > 0 ? [parentView.id] : [];
    this.dataChildOfIds.push(this.id);
    this.listEditSettings = { mode: 'dialog', editOnClick: false, editOnDoubleClick: false };
    this.listPaging = 'one-page';
  }

  private onParentValueGet(e: ParentDataArgs): string {
    var folderName = null;
    if (e.typeName?.length > 0 && e.row != null) {
      switch (e.typeName) {
        case 'ContractLegacy':
          folderName = e.row['hkontrakt'] ?? "/";
          break;
        case 'ContractSale':
          folderName = e.row['documentLibPath'] ?? "/";
          break;
        case DocumentLibFolderDto.name:
          const dto = e.row as DocumentLibFolderDto;
          folderName = dto?.name;
          break;
      }
    }
    const titleFields = this.parentView?.viewMetadata?.titleFields;
    if (titleFields?.length > 0 && e?.row) {
      this.title = CerDataService.title(this.parentView.viewMetadata, e.row);
    }
    //this.updateTitle(folderName, null)
    return folderName;
  }

  private onRefreshFromParentData(parentData: ParentDataArgs) {
    if (parentData.typeName?.length > 0 && parentData?.row != null) {
      switch (parentData.typeName) {
        case 'ContractLegacy':
          this.currentFolder = { name: parentData?.row['hkontrakt'], setAsRoot: true };
          break;
        case 'ContractSale':
          this.currentFolder = { name: parentData?.row['documentLibPath'], setAsRoot: true };
          break;
        case DocumentLibFolderDto.name:
          this.currentFolder = parentData.row;
          break;
      }
      if (this.currentFolder.setAsRoot) {
        this.rootFolder = this.currentFolder;
      }
    }
    else {
      this.currentFolder = { name: null, setAsRoot: true };
    }
  }

  private dataParentNext() {
    const parentDataArgs: ParentDataArgs = {
      typeName: DocumentLibFolderDto.name,
      row: this.currentFolder,
      id: this.id,
      topic: this.id,
      stable: true
    };
    this.context.formService.parentDataNext(parentDataArgs);
  }

  private currentFolder: DocumentLibFolderDto;
  private rootFolder: DocumentLibFolderDto;
  public selectFolder(folder: DocumentLibFolderDto) {
    if (this.currentFolder?.name == folder.name) {
      return;
    }
    if (folder?.name?.length > 0 || this.currentFolder?.name?.length > 0) {
      this.currentFolder = folder;
      //this.updateTitle(folder?.name);
      this.dataParentNext();
    }
  }

  private selectFolderBack() {
    const name = this.currentFolder?.name;
    if (name?.length > 0 && name != '/' && name != this.rootFolder?.name) {
      const fn = this.currentFolder.name;
      const splits: string[] = fn == "/" ? [] : fn.split("/");
      const name = splits.length > 1 ? splits.slice(0, splits.length - 1).join("/") : "/";
      this.selectFolder({ name: name, setAsRoot: false });
    }
  }

  private updateTitle(title: string, rowCount: number = null) {
    const finderTitle = (title?.length > 1 ? `${this.title}: ${title}` : this.title)
      + (rowCount == null ? '(henter)' : ` (${rowCount})`);
    this.commandUpdateTitle(finderTitle);
  }

  private grid: CerGridComponent;
  private rows: any = [];
  private onCommand(e: UiCommandEvent): void {
    switch (e.source) {
      case 'toolbar':
        switch (e.commandId) {
          case uiCmdBack.id:
            this.selectFolderBack();
            break;
          case uiCmdFolderAdd.id:
            this.folderAdd(e.appComponent);
            break;
        }
        break;
      case 'actionBegin':
        switch (e.actionType) {
          case 'dataBound':
            this.enableBackButton();
            this.grid = e.appComponent as CerGridComponent;
            //if (grid?.dataService.isOffline(grid.grid.dataSource) === false) {
            e.args.result?.forEach((a: any) => this.attachmentPrepare(a));
            this.rows = e.args.result;
            this.updateTitle(this.currentFolder?.name, this.rows.length);
            if (this.rows == null || this.rows.length == 0) {
              this.attachmentView?.select(null, null);
            }
            //}
            break;
          case 'rowDeselect':
            this.onAttachmentDeselect(e);
            break;
          case UiActionTypeEnum.Add:
            this.fileAdd(e.appComponent);
            e.cancel = true;
            e.args.cancel = true;
            break;
          case UiActionTypeEnum.Save:
            if (e.subAction == 'edit') {
              this.update(e.rowData, e.rowDataOriginal, e.appComponent);
            }
            break;
          case UiActionTypeEnum.Delete:
            const dtos = e.rowData;
            const dto: LinkedAttachmentDto = Array.isArray(dtos) ? (dtos.length > 0 ? dtos[0] : null) : dtos;
            if (dto?.originalFileName?.length > 0) {
              this.delete(dto, e.appComponent);
            }
            break;
        }
        break;
      case 'actionComplete':
        switch (e.actionType) {
          case 'created':
            this.grid = e.appComponent as CerGridComponent;
            break;
          case 'refreshFromParent':
            this.onRefreshFromParentData(e.args);
            break;
          case 'dataBound':
            const grid = e.appComponent as CerGridComponent;
            if (grid?.dataService.isOffline(grid.grid.dataSource) === false) {
              grid.commandService.dataSourceInitLocal(this.rows);
              grid.filterService.filterClear();
            }
            break;
          case 'rowSelect':
            this.onAttachmentSelect(e);
            break;
        }
        break;
    }
  }

  private enableBackButton() {
    const name = this.currentFolder?.name;
    const visible = (name?.length > 0 && name != '/' && name != this.rootFolder?.name);
    this.grid?.commandService.toolbarCommandVisible(uiCmdBackShort, visible);
  }

  private async folderAdd(c: ICerViewComponent): Promise<boolean> {
    var res = await this.context.ui.promptInputs(
      "<div>" + iconImageFolderHtml() + "</div>",
      "Opret folder",
      [{ id: 'fileName', label: "Foldernavn", placeholder: "Angiv navn", type: 'Input', element: null }]
    );
    if (res.action == 'ok') {
      const folderName = res.inputs[0].value;
      if (folderName?.length > 0) {
        this.create(folderName, null, false, c);
      }
    }
    return true;
  }

  private async fileAdd(c: ICerViewComponent): Promise<boolean> {
    var res = await this.context.ui.promptInputs(
      "<div>" + iconImageTextHtml() + "</div>",
      "Opret dokumentation",
      [
        { id: 'fileName', label: "Foldernavn", placeholder: "Angiv navn", type: 'Input', element: null },
        { id: 'extension', label: "Efternavn", placeholder: "Angiv efternavn", value: '.md', type: 'Input', disabled: true, element: null }
      ]
    );
    if (res.action == 'ok') {
      const fileName = res.inputs[0].value;
      const fileExtension = res.inputs[1].value;
      if (fileName?.length > 0) {
        this.create(fileName, fileExtension, true, c);
      }
    }
    return true;
  }

  private create(fileName: string, fileExtension: string, isFile: boolean, c: ICerViewComponent) {
    const folderName = this.currentFolder?.name;
    const fileNameCreate = fileName + (fileExtension ?? '');

    const ui = this.context.ui;
    ui.operationBegin();
    const client = this.context.injector.get(DataLinkAttachmentClient);
    ((isFile) ? client.fileCreate(documentLibEntityName, this.docLib.id, folderName, fileNameCreate, this.docLib.token, true)
      : client.folderCreate(documentLibEntityName, this.docLib.id, folderName, fileNameCreate, this.docLib.token, true)).subscribe({
        next: (dtos) => {
          this.attachmentsBind(c, dtos);
          ui.operationEnd();
        },
        error: (error) => {
          ui.operationEndError(error);
        }
      });
  }

  private update(dto: LinkedAttachmentDto, dtoOrig: LinkedAttachmentDto, c: ICerViewComponent) {
    if (dto?.originalFileName?.length > 0 && dtoOrig.originalFileName?.length > 0) {
      const fileName = this.fileNameExt(dtoOrig);
      const fileNameNew = this.fileNameExt(dto);
      if (fileName != fileNameNew) {
        const client = this.context.injector.get(DataLinkAttachmentClient);
        const ui = this.context.ui;
        ui.operationBegin();
        client.fileRename(documentLibEntityName, this.docLib.id, dto.folderName, fileName, fileNameNew, this.docLib.token, false, true).subscribe({
          next: (dtos) => {
            this.attachmentsBind(c, dtos);
            ui.operationEnd();
          },
          error: (error) => {
            ui.operationEndError(error);
          }
        });
      }
    }
  }

  private delete(dto: LinkedAttachmentDto, c: ICerViewComponent) {
    const ui = this.context.ui;
    const fileName = this.fileNameExt(dto);
    ui.operationBegin();

    const client = this.context.injector.get(DataLinkAttachmentClient);
    client.delete(documentLibEntityName, this.docLib.id, dto.folderName, fileName, this.docLib.token, true).subscribe({
      next: dtos => {
        ui.operationEnd();
        this.attachmentsBind(c, dtos);
        return dtos;
      },
      error: (error) => ui.operationEndError(error)
    });
  }

  private fileNameExt(dto: LinkedAttachmentDto, originalFileName: boolean = true): string {
    return originalFileName ?
      dto.originalFileName + (dto.originalFileExtension ?? '') :
      dto.fileName + (dto.fileExtension ?? '');
  }

  private attachmentPrepare(a: any /*LinkedAttachmentDto*/) {
    if (a == null) {
      return;
    }
    a.originalUrl ??= a.url;
    a.originalFileName ??= a.fileName;
    a.originalFileExtension ??= a.fileExtension;
    a.originalDocType ??= a.docType;
  }

  private onAttachmentSelect(e: UiCommandEvent) {
    const attachment: LinkedAttachmentDto = e.rowData;
    this.attachmentView?.select(attachment, this.attachmentUploadUrlGet(true));
    if (isDocTypeFolder(attachment?.docType) && e?.columnName == 'originalDocType') {
      this.onAttachmentFolderOpen(e, attachment);
    }
  }

  private onAttachmentDeselect(e: UiCommandEvent) {
    this.attachmentView?.select(null, this.attachmentUploadUrlGet(true));
  }

  private onAttachmentFolderOpen(e: UiCommandEvent, attachment: LinkedAttachmentDto) {
    var grid = e.appComponent as CerGridComponent;
    grid?.selection?.rowSelectedClear();
    const name = (attachment.folderName?.length > 0 && attachment.folderName != '/'
      ? attachment.folderName + '/'
      : '') +
      (attachment?.fileName ?? '');
    this.selectFolder({ name: name, setAsRoot: false });
  }

  private attachmentUploadUrlGet(overwriteExisting: boolean): string {
    var encoded: string[] = [];
    (this.viewMetadata.dataApiParams as string[]).concat([
      "$folderName=" + (this.currentFolder?.name ?? ""),
      "$overwriteExisting=" + (overwriteExisting ? "1" : "0")
    ]).forEach(p => {
      const ps = p.split("=");
      ps.forEach((v, i) => { if (i > 0) { ps[i] = encodeURIComponent(ps[i]) } });
      encoded.push(ps.join('='));
    });
    return this.viewMetadata.dataApiUrl + "/FormUpload?" + encoded.join("&");
  }

  private attachmentsBind(c: ICerViewComponent, dtos: LinkedAttachmentDto[]): any {
    const g = c as CerGridComponent;
    if (g) {
      (<any>g.grid.dataSource).dataSource.json = dtos;
      g.research();
      //g.grid.editModule.updateRow(0,dtos[0]);
    }
  }
}
